import { useEffect, useState } from 'react';

const COUNTRY_TO_CURRENCY = {
  NG: 'NGN', KE: 'KES', GH: 'GHS', ZA: 'ZAR', UG: 'UGX', TZ: 'TZS',
  ET: 'ETB', EG: 'EGP', MA: 'MAD', CM: 'XAF', SN: 'XOF', CI: 'XOF',
  GB: 'GBP', DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR',
  PT: 'EUR', BE: 'EUR', AT: 'EUR', FI: 'EUR', IE: 'EUR', GR: 'EUR',
  AU: 'AUD', CA: 'CAD', JP: 'JPY', CH: 'CHF', SG: 'SGD', IN: 'INR',
  AE: 'AED', SA: 'SAR', BR: 'BRL', MX: 'MXN', MY: 'MYR', PH: 'PHP',
  ID: 'IDR', TH: 'THB', VN: 'VND', PK: 'PKR', BD: 'BDT', LK: 'LKR',
  RU: 'RUB', PL: 'PLN', SE: 'SEK', NO: 'NOK', DK: 'DKK', HU: 'HUF',
  CZ: 'CZK', RO: 'RON', TR: 'TRY', UA: 'UAH', CN: 'CNY', HK: 'HKD',
  KR: 'KRW', TW: 'TWD', NZ: 'NZD', IL: 'ILS', AR: 'ARS', CL: 'CLP',
  CO: 'COP', PE: 'PEN', ZW: 'ZWL', ZM: 'ZMW', MW: 'MWK', RW: 'RWF',
};

// Module-level cache — survives re-renders, fetches only once per page load
let _cache = null;
let _promise = null;

const detect = async () => {
  if (_cache) return _cache;
  if (_promise) return _promise;

  _promise = (async () => {
    let country = null;

    // 1. Cloudflare trace — IP-based, CORS-safe, no key needed
    try {
      const res = await fetch('https://cloudflare.com/cdn-cgi/trace', { cache: 'no-store' });
      const text = await res.text();
      const m = text.match(/^loc=([A-Z]{2})/m);
      country = m?.[1] || null;
    } catch {}

    // 2. Browser locale fallback — works offline, less accurate
    if (!country) {
      try {
        const langs = Array.isArray(navigator.languages) && navigator.languages.length
          ? navigator.languages
          : [navigator.language || 'en'];
        for (const lang of langs) {
          const region = new Intl.Locale(lang).region;
          if (region) { country = region; break; }
        }
      } catch {}
    }

    const code = country ? COUNTRY_TO_CURRENCY[country] : null;
    if (!code || code === 'USD') { _cache = {}; return _cache; }

    // 3. Fetch exchange rate
    let rate = null;
    try {
      const res = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await res.json();
      rate = data?.rates?.[code] ?? null;
    } catch {}

    _cache = { code, rate };
    return _cache;
  })();

  return _promise;
};

export function useCurrency() {
  const [state, setState] = useState({ code: _cache?.code || null, rate: _cache?.rate || null });

  useEffect(() => {
    if (_cache?.code) return; // already resolved, state is already set
    detect().then(result => {
      if (result?.code) setState({ code: result.code, rate: result.rate });
    });
  }, []);

  return state;
}
